import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, Loader2, PlayCircle, RefreshCw, CheckCircle2, AlertTriangle,
  Globe, Network, Server, Sparkles, Import, Clock, X, CalendarClock,
  Plus, Minus, GitCompare, Trash2, Pause, Play, History, Undo2,
} from "lucide-react";
import { cn } from "@/lib/util";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SOURCE_LABEL = {
  crtsh: "crt.sh",
  hackertarget: "HackerTarget",
  shodan: "Shodan",
  censys: "Censys",
};

const KIND_LABEL = {
  domain: "Dominio",
  subdomain: "Subdominio",
  ip: "IP pública",
};

const KIND_ICON = {
  domain: Globe,
  subdomain: Network,
  ip: Server,
};

const INTERVAL_OPTIONS = [
  { hours: 24,  label: "Diaria" },
  { hours: 72,  label: "Cada 3 días" },
  { hours: 168, label: "Semanal" },
  { hours: 720, label: "Mensual" },
];
const INTERVAL_LABEL = Object.fromEntries(INTERVAL_OPTIONS.map(o => [o.hours, o.label]));

export default function AssetDiscovery() {
  const [availability, setAvailability] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [rootDomain, setRootDomain] = useState("");
  const [rootDomainTouched, setRootDomainTouched] = useState(false);
  const [running, setRunning] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [activeDiff, setActiveDiff] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [filterSource, setFilterSource] = useState("all");
  const [filterKind, setFilterKind] = useState("all");
  const [filterNewOnly, setFilterNewOnly] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importTargetClient, setImportTargetClient] = useState("");
  // Undo-import banner state: last successful import + timeout id
  const [lastImport, setLastImport] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState(168);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDetailId, setHistoryDetailId] = useState(null);
  const [historyDetailJob, setHistoryDetailJob] = useState(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  // Import history modal (all past imports — active or reverted, revertible by row)
  const [importsOpen, setImportsOpen] = useState(false);
  const [imports, setImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);

  const loadJobs = useCallback(async (clientId) => {
    if (!clientId) { setJobs([]); return; }
    const r = await api.get(`/asm/discovery/jobs?client_id=${clientId}&limit=20`);
    setJobs(r.data || []);
  }, []);

  const loadSchedules = useCallback(async (clientId) => {
    if (!clientId) { setSchedules([]); return; }
    const r = await api.get(`/asm/discovery/schedules?client_id=${clientId}`);
    setSchedules(r.data || []);
  }, []);

  const loadJobDetail = useCallback(async (jobId) => {
    const r = await api.get(`/asm/discovery/jobs/${jobId}`);
    setActiveJob(r.data);
    setActiveDiff(null);
    setShowDiff(false);
    return r.data;
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/asm/discovery/availability"),
      api.get("/clients"),
    ]).then(([av, cl]) => {
      setAvailability(av.data.sources || {});
      setClients(cl.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedClient) {
      setRootDomain(""); setRootDomainTouched(false);
      setSchedules([]);
      return;
    }
    const c = clients.find(x => x.id === selectedClient);
    if (c && !rootDomainTouched) {
      const first = (c.root_domains || [])[0] || "";
      setRootDomain(first);
    }
    loadJobs(selectedClient);
    loadSchedules(selectedClient);
    setActiveJob(null);
    setSelectedIds(new Set());
  }, [selectedClient, clients, loadJobs, loadSchedules, rootDomainTouched]);

  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status !== "queued" && activeJob.status !== "running") return;
    const id = setInterval(async () => {
      try {
        const updated = await loadJobDetail(activeJob.id);
        if (updated.status === "done" || updated.status === "error") {
          loadJobs(selectedClient);
          loadSchedules(selectedClient);
        }
      } catch (e) {}
    }, 2500);
    return () => clearInterval(id);
  }, [activeJob, loadJobDetail, loadJobs, loadSchedules, selectedClient]);

  // Auto-load the latest completed job whenever the jobs list refreshes so
  // the main view always reflects "current/latest scan" without user action.
  useEffect(() => {
    if (activeJob) return;
    if (!jobs.length) return;
    const latestDone = jobs.find(j => j.status === "done") || jobs[0];
    if (latestDone) loadJobDetail(latestDone.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  const clientNameById = useMemo(
    () => Object.fromEntries((clients || []).map(c => [c.id, c.name])),
    [clients]
  );

  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryDetailId(null);
    setHistoryDetailJob(null);
    setHistoryLoading(true);
    try {
      // Fetch across ALL clients of the tenant so the modal is a global view.
      const r = await api.get("/asm/discovery/jobs?limit=100");
      setHistoryJobs(r.data || []);
    } catch (ex) {
      toast.error("No se pudo cargar el historial");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadImports = useCallback(async () => {
    setImportsLoading(true);
    try {
      const r = await api.get("/asm/discovery/imports?limit=200");
      setImports(r.data || []);
    } catch (ex) {
      toast.error("No se pudo cargar el historial de importaciones");
    } finally { setImportsLoading(false); }
  }, []);

  const openImports = useCallback(() => {
    setImportsOpen(true);
    loadImports();
  }, [loadImports]);

  const revertImport = useCallback(async (imp) => {
    if (imp.reverted_at) return;
    if (!window.confirm(
      `¿Revertir esta importación?\n\n` +
      `${imp.total_imported} activo(s) serán eliminados del cliente ` +
      `${imp.organization_name || "destino"}. Acción inmediata.`
    )) return;
    setRevertingId(imp.id);
    try {
      const r = await api.post(`/asm/discovery/imports/${imp.id}/undo`);
      const rem = r.data.removed || {};
      const parts = [];
      if (rem.domains) parts.push(`${rem.domains} dominio(s)`);
      if (rem.subdomains) parts.push(`${rem.subdomains} subdominio(s)`);
      if (rem.ips) parts.push(`${rem.ips} IP(s)`);
      toast.success(`Importación revertida · ${parts.join(" · ") || "sin cambios"}`);
      // Clear the sticky "last import" banner if it refers to the reverted row.
      setLastImport((li) => (li && li.id === imp.id ? null : li));
      await loadImports();
      if (activeJob) await loadJobDetail(activeJob.id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo revertir");
    } finally { setRevertingId(null); }
  }, [loadImports, activeJob, loadJobDetail]);

  const openHistoryDetail = useCallback(async (jobId) => {
    setHistoryDetailId(jobId);
    setHistoryDetailLoading(true);
    try {
      const r = await api.get(`/asm/discovery/jobs/${jobId}`);
      setHistoryDetailJob(r.data);
    } catch (ex) {
      toast.error("No se pudo cargar el detalle");
      setHistoryDetailId(null);
    } finally {
      setHistoryDetailLoading(false);
    }
  }, []);

  const handleRun = async () => {
    if (!selectedClient) { toast.error("Selecciona un cliente"); return; }
    setRunning(true);
    try {
      const payload = { client_id: selectedClient };
      if (rootDomain?.trim()) payload.root_domain = rootDomain.trim();
      const r = await api.post("/asm/discovery/run", payload);
      toast.success(`Descubrimiento encolado sobre ${r.data.root_domain}`);
      await loadJobs(selectedClient);
      await loadJobDetail(r.data.job_id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo iniciar el descubrimiento");
    } finally {
      setRunning(false);
    }
  };

  const handleImport = async () => {
    if (!activeJob || selectedIds.size === 0) return;
    // Pre-fill the target client with the job's owner client and open the
    // confirmation modal so the user can pick a DIFFERENT client if needed.
    setImportTargetClient(activeJob.organization_id || selectedClient || "");
    setImportOpen(true);
  };

  const confirmImport = async () => {
    if (!activeJob || selectedIds.size === 0) return;
    if (!importTargetClient) {
      toast.error("Selecciona un cliente destino");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post(`/asm/discovery/jobs/${activeJob.id}/import`, {
        asset_ids: Array.from(selectedIds),
        target_client_id: importTargetClient,
      });
      const parts = [];
      if (r.data.imported?.domains) parts.push(`${r.data.imported.domains} dominio(s)`);
      if (r.data.imported?.subdomains) parts.push(`${r.data.imported.subdomains} subdominio(s)`);
      if (r.data.imported?.ips) parts.push(`${r.data.imported.ips} IP(s)`);
      const targetName = clients.find(c => c.id === importTargetClient)?.name || "cliente";
      toast.success(`Importado a ${targetName} · ${parts.join(" · ") || "0 activos"}`);
      // Persist the last-import handle so the operator can undo it
      setLastImport({
        id: r.data.import_id,
        targetClientId: importTargetClient,
        targetClientName: targetName,
        summary: r.data.imported,
        total: r.data.total_imported,
        at: Date.now(),
      });
      setSelectedIds(new Set());
      setImportOpen(false);
      await loadJobDetail(activeJob.id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo importar");
    } finally {
      setImporting(false);
    }
  };

  const undoLastImport = async () => {
    if (!lastImport?.id) return;
    if (!window.confirm(
      `¿Revertir la última importación?\n\n` +
      `${lastImport.total} activo(s) serán eliminados del cliente ` +
      `${lastImport.targetClientName}. Esta acción es inmediata.`
    )) return;
    setUndoing(true);
    try {
      const r = await api.post(`/asm/discovery/imports/${lastImport.id}/undo`);
      const rem = r.data.removed || {};
      const parts = [];
      if (rem.domains) parts.push(`${rem.domains} dominio(s)`);
      if (rem.subdomains) parts.push(`${rem.subdomains} subdominio(s)`);
      if (rem.ips) parts.push(`${rem.ips} IP(s)`);
      toast.success(`Importación revertida · ${parts.join(" · ") || "sin cambios"}`);
      setLastImport(null);
      if (activeJob) await loadJobDetail(activeJob.id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo deshacer");
    } finally {
      setUndoing(false);
    }
  };

  const loadDiff = async () => {
    if (!activeJob) return;
    setShowDiff(true);
    try {
      const r = await api.get(`/asm/discovery/jobs/${activeJob.id}/diff`);
      setActiveDiff(r.data);
    } catch (ex) {
      toast.error("No se pudo cargar el diff");
      setShowDiff(false);
    }
  };

  const createSchedule = async () => {
    if (!selectedClient) { toast.error("Selecciona un cliente"); return; }
    try {
      await api.post("/asm/discovery/schedules", {
        client_id: selectedClient,
        root_domain: rootDomain?.trim() || null,
        interval_hours: scheduleInterval,
      });
      toast.success("Programación creada");
      setShowScheduleForm(false);
      await loadSchedules(selectedClient);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo crear la programación");
    }
  };

  const toggleSchedule = async (sched) => {
    try {
      await api.patch(`/asm/discovery/schedules/${sched.id}`, { enabled: !sched.enabled });
      await loadSchedules(selectedClient);
    } catch (ex) {
      toast.error("No se pudo actualizar la programación");
    }
  };

  const deleteScheduleRow = async (sched) => {
    if (!window.confirm(`¿Eliminar la programación de ${sched.root_domain}?`)) return;
    try {
      await api.delete(`/asm/discovery/schedules/${sched.id}`);
      await loadSchedules(selectedClient);
    } catch (ex) {
      toast.error("No se pudo eliminar");
    }
  };

  const runScheduleNow = async (sched) => {
    try {
      const r = await api.post(`/asm/discovery/schedules/${sched.id}/run`);
      toast.success("Descubrimiento encolado");
      await loadJobs(selectedClient);
      await loadSchedules(selectedClient);
      if (r.data?.job_id) await loadJobDetail(r.data.job_id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo ejecutar ahora");
    }
  };

  const filteredResults = useMemo(() => {
    const results = activeJob?.results || [];
    return results.filter(a => {
      if (filterNewOnly && !a.is_new) return false;
      if (filterKind !== "all" && a.kind !== filterKind) return false;
      if (filterSource !== "all" && !(a.sources || []).includes(filterSource)) return false;
      return true;
    });
  }, [activeJob, filterKind, filterSource, filterNewOnly]);

  const toggleId = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleAllVisible = () => {
    if (filteredResults.every(a => selectedIds.has(a.id))) {
      const next = new Set(selectedIds);
      filteredResults.forEach(a => next.delete(a.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      filteredResults.forEach(a => { if (a.is_new && !a.imported) next.add(a.id); });
      setSelectedIds(next);
    }
  };

  const counts = activeJob?.counts || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="asset-discovery-page">
      <header>
        <div className="overline mb-2">Attack Surface Management · Discovery</div>
        <h1 className="font-display font-bold tracking-tighter"
            style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>
          Descubrimiento de Activos
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Enumera la superficie de ataque externa consultando en paralelo cuatro fuentes
          (crt.sh, DNSDumpster, Shodan y Censys). Consolida subdominios, IPs, ASN,
          puertos y servicios expuestos, marcando los <span className="text-primary">activos nuevos</span> que
          aún no están en el inventario. También puedes <span className="text-primary">agendar corridas periódicas</span> y
          comparar la superficie con la ejecución anterior.
        </p>
      </header>

      {/* Source availability */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="source-availability">
        {["crtsh", "hackertarget", "shodan", "censys"].map(s => {
          const ok = availability?.[s];
          return (
            <div key={s}
                 className={cn("border px-3 py-2 flex items-center gap-2",
                               ok ? "border-sev-low/40 bg-sev-low/5" : "border-sev-medium/40 bg-sev-medium/5")}>
              <div className={cn("w-2 h-2 rounded-full",
                                 ok ? "bg-sev-low animate-pulse" : "bg-sev-medium")} />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {SOURCE_LABEL[s]}
                </div>
                <div className={cn("text-xs", ok ? "text-sev-low" : "text-sev-medium")}>
                  {ok ? "Disponible" : "Sin API key"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Run form */}
      <section className="border border-border bg-card/60 p-5 space-y-4" data-testid="discovery-form">
        <div className="flex items-center gap-2">
          <Radar className="w-4 h-4 text-primary" />
          <h2 className="font-display text-lg">Nuevo descubrimiento</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Cliente
            </label>
            <select
              value={selectedClient}
              onChange={(e) => { setSelectedClient(e.target.value); setRootDomainTouched(false); }}
              data-testid="discovery-client-select"
              className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
            >
              <option value="">-- Selecciona un cliente --</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Dominio raíz (auto-sugerido · editable)
            </label>
            <input
              type="text"
              value={rootDomain}
              onChange={(e) => { setRootDomain(e.target.value); setRootDomainTouched(true); }}
              placeholder="ej: acme-demo.com"
              data-testid="discovery-root-input"
              className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleRun}
            disabled={!selectedClient || running}
            data-testid="discovery-run-btn"
            className={cn(
              "px-4 py-2 flex items-center gap-2 text-sm font-mono uppercase tracking-widest border transition-colors",
              !selectedClient || running
                ? "border-border text-muted-foreground cursor-not-allowed"
                : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            )}
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            {running ? "Encolando…" : "Ejecutar ahora"}
          </button>
          <button
            onClick={() => setShowScheduleForm(v => !v)}
            disabled={!selectedClient}
            data-testid="show-schedule-form-btn"
            className={cn(
              "px-4 py-2 flex items-center gap-2 text-sm font-mono uppercase tracking-widest border transition-colors",
              !selectedClient
                ? "border-border text-muted-foreground cursor-not-allowed"
                : "border-border text-foreground hover:border-primary hover:text-primary"
            )}
          >
            <CalendarClock className="w-4 h-4" />
            Programar…
          </button>
        </div>

        {showScheduleForm && selectedClient && (
          <div className="border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="schedule-form">
            <div className="text-xs font-mono uppercase tracking-widest text-primary">
              Nueva programación
            </div>
            <div className="text-xs text-muted-foreground">
              Se ejecutará automáticamente sobre <span className="text-foreground font-mono">{rootDomain || "—"}</span>{" "}
              y comparará el resultado con la corrida anterior. Si aparecen ≥5 activos
              nuevos se dispara una alerta por email.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={scheduleInterval}
                onChange={(e) => setScheduleInterval(Number(e.target.value))}
                data-testid="schedule-interval-select"
                className="bg-background border border-border px-3 py-2 text-sm font-mono"
              >
                {INTERVAL_OPTIONS.map(o => (
                  <option key={o.hours} value={o.hours}>{o.label}</option>
                ))}
              </select>
              <button
                onClick={createSchedule}
                data-testid="schedule-create-btn"
                className="px-3 py-2 text-xs font-mono uppercase tracking-widest border border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                Crear programación
              </button>
              <button
                onClick={() => setShowScheduleForm(false)}
                className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Ver historial — abre modal con TODAS las corridas del tenant */}
        <div className="pt-3 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={openHistory}
            data-testid="open-history-modal-btn"
            className="px-4 py-3 flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-widest border border-border text-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
          >
            <History className="w-4 h-4" />
            Historial de descubrimientos
          </button>
          <button
            onClick={openImports}
            data-testid="open-imports-modal-btn"
            className="px-4 py-3 flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-widest border border-border text-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
          >
            <Import className="w-4 h-4" />
            Historial de importaciones
          </button>
        </div>
      </section>

      {/* Historial de importaciones — modal con todas las imports pasadas */}
      <Dialog open={importsOpen} onOpenChange={setImportsOpen}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 sm:rounded-none"
          data-testid="imports-modal"
        >
          <DialogHeader className="p-5 border-b border-border">
            <DialogTitle className="flex items-center gap-2 font-display">
              <Import className="w-4 h-4 text-primary" />
              Historial de importaciones
            </DialogTitle>
            <DialogDescription>
              Cada importación se registra individualmente. Puedes revertir cualquiera —
              activa o no — no solo la última.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-5">
            {importsLoading && (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando…
              </div>
            )}
            {!importsLoading && imports.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <Import className="w-8 h-8 mx-auto mb-3 opacity-40" />
                <div className="text-sm">Todavía no hay importaciones registradas.</div>
              </div>
            )}
            {!importsLoading && imports.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono" data-testid="imports-table">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground uppercase tracking-widest text-[10px]">
                      <th className="p-2">Fecha</th>
                      <th className="p-2">Cliente destino</th>
                      <th className="p-2 text-right">Dominios</th>
                      <th className="p-2 text-right">Subdom.</th>
                      <th className="p-2 text-right">IPs</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2">Estado</th>
                      <th className="p-2 text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imports.map((imp) => {
                      const reverted = !!imp.reverted_at;
                      return (
                        <tr key={imp.id}
                            data-testid={`import-row-${imp.id}`}
                            className={cn(
                              "border-b border-border/40",
                              reverted && "opacity-50"
                            )}>
                          <td className="p-2 text-foreground whitespace-nowrap">
                            {new Date(imp.created_at).toLocaleString()}
                          </td>
                          <td className="p-2 text-foreground">
                            {imp.organization_name || (imp.organization_id || "").slice(0, 8)}
                          </td>
                          <td className="p-2 text-right">{imp.summary?.domains ?? 0}</td>
                          <td className="p-2 text-right">{imp.summary?.subdomains ?? 0}</td>
                          <td className="p-2 text-right">{imp.summary?.ips ?? 0}</td>
                          <td className="p-2 text-right text-foreground">{imp.total_imported}</td>
                          <td className="p-2">
                            {reverted ? (
                              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-sev-medium/10 text-sev-medium border border-sev-medium/30">
                                Revertida
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-sev-low/10 text-sev-low border border-sev-low/30">
                                Activa
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            {!reverted && (
                              <button
                                onClick={() => revertImport(imp)}
                                disabled={revertingId === imp.id}
                                data-testid={`revert-import-${imp.id}`}
                                className={cn(
                                  "inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-2 py-1",
                                  revertingId === imp.id
                                    ? "border-border text-muted-foreground cursor-not-allowed"
                                    : "border-sev-medium text-sev-medium hover:bg-sev-medium hover:text-background"
                                )}
                              >
                                {revertingId === imp.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Undo2 className="w-3 h-3" />}
                                Revertir
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedules list */}
      {selectedClient && schedules.length > 0 && (
        <section className="border border-border bg-card/60 p-5 space-y-3" data-testid="discovery-schedules-list">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" />
            <h2 className="font-display text-lg">Programaciones activas</h2>
          </div>
          <div className="space-y-2">
            {schedules.map(s => (
              <div key={s.id}
                   data-testid={`schedule-row-${s.id}`}
                   className={cn(
                     "border px-3 py-3 text-xs font-mono grid grid-cols-1 md:grid-cols-6 gap-2 items-center",
                     s.enabled ? "border-border" : "border-border/40 bg-muted/10 opacity-60"
                   )}>
                <div className="md:col-span-2 truncate">
                  <div className="text-foreground">{s.root_domain}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {INTERVAL_LABEL[s.interval_hours] || `${s.interval_hours}h`}
                  </div>
                </div>
                <div>
                  Próxima:{" "}
                  <span className="text-foreground">
                    {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}
                  </span>
                </div>
                <div>
                  {s.last_diff ? (
                    <span data-testid={`schedule-diff-${s.id}`}>
                      Δ <span className="text-primary">+{s.last_diff.added ?? 0}</span>{" "}
                      <span className="text-sev-medium">-{s.last_diff.removed ?? 0}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Sin diff aún</span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2 md:col-span-2">
                  {s.last_job_id && (
                    <button
                      onClick={() => loadJobDetail(s.last_job_id)}
                      className="text-muted-foreground hover:text-primary"
                      title="Ver última corrida"
                      data-testid={`schedule-view-last-${s.id}`}
                    >
                      <GitCompare className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => runScheduleNow(s)}
                    className="text-muted-foreground hover:text-primary"
                    title="Ejecutar ahora"
                    data-testid={`schedule-run-now-${s.id}`}
                  >
                    <PlayCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleSchedule(s)}
                    className="text-muted-foreground hover:text-primary"
                    title={s.enabled ? "Pausar" : "Reanudar"}
                    data-testid={`schedule-toggle-${s.id}`}
                  >
                    {s.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => deleteScheduleRow(s)}
                    className="text-muted-foreground hover:text-sev-critical"
                    title="Eliminar"
                    data-testid={`schedule-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Undo-import banner — sticky under toolbar while lastImport is set */}
      {lastImport && (
        <div className="border border-primary/40 bg-primary/5 p-3 flex items-center justify-between gap-3"
             data-testid="undo-import-banner">
          <div className="flex items-center gap-2 text-xs font-mono">
            <CheckCircle2 className="w-4 h-4 text-sev-low flex-shrink-0" />
            <span className="text-foreground">
              Última importación · <span className="text-primary">{lastImport.total} activo(s)</span> a{" "}
              <span className="text-foreground">{lastImport.targetClientName}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={undoLastImport}
              disabled={undoing}
              data-testid="undo-import-btn"
              className={cn(
                "px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest border transition-colors",
                undoing
                  ? "border-border text-muted-foreground cursor-not-allowed"
                  : "border-sev-medium text-sev-medium hover:bg-sev-medium hover:text-background"
              )}
            >
              {undoing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
              Deshacer importación
            </button>
            <button
              onClick={() => setLastImport(null)}
              className="p-1 text-muted-foreground hover:text-foreground"
              title="Descartar aviso"
              data-testid="undo-import-dismiss"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Import confirmation modal — user picks the destination client */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md" data-testid="import-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Import className="w-4 h-4 text-primary" />
              Confirmar importación
            </DialogTitle>
            <DialogDescription>
              Vas a importar <span className="text-foreground font-mono">{selectedIds.size}</span>{" "}
              activo(s) seleccionados al inventario. Elige el cliente al que quedarán vinculados.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Cliente destino
              </label>
              <select
                value={importTargetClient}
                onChange={(e) => setImportTargetClient(e.target.value)}
                data-testid="import-client-select"
                className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
              >
                <option value="">— Selecciona un cliente —</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.id === activeJob?.organization_id ? "  (cliente del descubrimiento)" : ""}
                  </option>
                ))}
              </select>
              {activeJob?.organization_id
               && importTargetClient
               && importTargetClient !== activeJob.organization_id && (
                <div className="mt-2 flex items-start gap-2 text-[10px] text-sev-medium font-mono">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>
                    Los activos quedarán vinculados a un cliente distinto del que originó
                    el descubrimiento. Confirma que este es el cliente correcto.
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button
                onClick={() => setImportOpen(false)}
                disabled={importing}
                data-testid="import-cancel-btn"
                className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                onClick={confirmImport}
                disabled={importing || !importTargetClient}
                data-testid="import-confirm-btn"
                className={cn(
                  "px-4 py-2 flex items-center gap-2 text-xs font-mono uppercase tracking-widest border transition-colors",
                  importing || !importTargetClient
                    ? "border-border text-muted-foreground cursor-not-allowed"
                    : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                )}
              >
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Import className="w-3 h-3" />}
                {importing ? "Importando…" : `Importar ${selectedIds.size} activo(s)`}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historial global — modal con todas las corridas del tenant */}
      <Dialog open={historyOpen} onOpenChange={(v) => {
        setHistoryOpen(v);
        if (!v) { setHistoryDetailId(null); setHistoryDetailJob(null); }
      }}>
        <DialogContent
          className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col p-0 sm:rounded-none"
          data-testid="history-modal"
        >
          <DialogHeader className="p-5 border-b border-border">
            <DialogTitle className="flex items-center gap-2 font-display">
              {historyDetailId ? (
                <>
                  <button
                    onClick={() => { setHistoryDetailId(null); setHistoryDetailJob(null); }}
                    className="text-muted-foreground hover:text-primary text-xs font-mono uppercase tracking-widest"
                    data-testid="history-back-btn"
                  >
                    ← Volver
                  </button>
                  <span>Detalle · {historyDetailJob?.root_domain || "…"}</span>
                </>
              ) : (
                <>
                  <History className="w-4 h-4 text-primary" />
                  Historial de descubrimientos
                </>
              )}
            </DialogTitle>
            {!historyDetailId && (
              <DialogDescription>
                Todas las corridas del tenant ordenadas por fecha descendente.
                Selecciona una para ver sus resultados completos aquí mismo.
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-5">
            {/* --------- LIST VIEW --------- */}
            {!historyDetailId && (
              <>
                {historyLoading && (
                  <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando historial…
                  </div>
                )}
                {!historyLoading && historyJobs.length === 0 && (
                  <div className="text-center py-16 text-muted-foreground">
                    <History className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    <div className="text-sm">No hay corridas registradas todavía.</div>
                    <div className="text-xs mt-1">Ejecuta el primer descubrimiento en la sección de arriba.</div>
                  </div>
                )}
                {!historyLoading && historyJobs.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono" data-testid="history-table">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground uppercase tracking-widest text-[10px]">
                          <th className="p-2">Fecha</th>
                          <th className="p-2">Cliente</th>
                          <th className="p-2">Dominio raíz</th>
                          <th className="p-2">Estado</th>
                          <th className="p-2 text-right">Total</th>
                          <th className="p-2 text-right">Nuevos</th>
                          <th className="p-2 text-right">Subdom.</th>
                          <th className="p-2 text-right">IPs</th>
                          <th className="p-2">Origen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyJobs.map((j) => (
                          <tr
                            key={j.id}
                            onClick={() => openHistoryDetail(j.id)}
                            data-testid={`history-row-${j.id}`}
                            className="border-b border-border/40 hover:bg-primary/5 cursor-pointer transition-colors"
                          >
                            <td className="p-2 text-foreground whitespace-nowrap">
                              {new Date(j.created_at).toLocaleString()}
                            </td>
                            <td className="p-2 text-foreground">
                              {clientNameById[j.organization_id] || (
                                <span className="text-muted-foreground italic">
                                  {(j.organization_id || "").slice(0, 8)}…
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-muted-foreground truncate max-w-[200px]"
                                title={j.root_domain}>
                              {j.root_domain}
                            </td>
                            <td className="p-2">
                              <StatusBadge status={j.status} />
                            </td>
                            <td className="p-2 text-right text-foreground">{j.counts?.total ?? 0}</td>
                            <td className="p-2 text-right">
                              <span className={cn(
                                "font-mono",
                                (j.counts?.new ?? 0) > 0 ? "text-primary" : "text-muted-foreground"
                              )}>
                                {j.counts?.new ?? 0}
                              </span>
                            </td>
                            <td className="p-2 text-right text-muted-foreground">
                              {j.counts?.subdomains ?? 0}
                            </td>
                            <td className="p-2 text-right text-muted-foreground">
                              {j.counts?.ips ?? 0}
                            </td>
                            <td className="p-2">
                              {j.schedule_id ? (
                                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30">
                                  Programado
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-[10px]">Manual</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* --------- DETAIL VIEW (inside the same modal) --------- */}
            {historyDetailId && (
              <div className="space-y-4">
                {historyDetailLoading && (
                  <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando detalle…
                  </div>
                )}
                {!historyDetailLoading && historyDetailJob && (
                  <HistoryJobDetail
                    job={historyDetailJob}
                    clientName={clientNameById[historyDetailJob.organization_id]}
                  />
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Active job details */}
      {activeJob && (
        <section className="border border-border bg-card/60 p-5 space-y-4" data-testid="active-job-detail">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline mb-1">Resultado del descubrimiento</div>
              <h2 className="font-display text-2xl">{activeJob.root_domain}</h2>
              <div className="text-xs font-mono text-muted-foreground mt-1">
                Job {activeJob.id.slice(0, 8)} · <StatusBadge status={activeJob.status} />
                {activeJob.finished_at && (
                  <> · Finalizado {new Date(activeJob.finished_at).toLocaleString()}</>
                )}
                {activeJob.schedule_id && <> · Ejecución programada</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeJob.status === "done" && (
                <button onClick={loadDiff}
                        data-testid="load-diff-btn"
                        className="px-3 py-1 border border-border text-xs font-mono uppercase tracking-widest text-foreground hover:border-primary hover:text-primary flex items-center gap-1">
                  <GitCompare className="w-3 h-3" /> Diff vs anterior
                </button>
              )}
              <button onClick={() => setActiveJob(null)} className="text-muted-foreground hover:text-foreground p-1"
                      data-testid="active-job-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Diff panel */}
          {showDiff && activeDiff && (
            <div className="border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="diff-panel">
              <div className="flex items-center gap-2">
                <GitCompare className="w-4 h-4 text-primary" />
                <h3 className="font-display text-sm">Comparativa vs ejecución anterior</h3>
              </div>
              {!activeDiff.has_previous ? (
                <div className="text-xs text-muted-foreground">
                  Aún no hay corrida anterior para comparar. Ejecuta el descubrimiento de nuevo
                  (o espera al próximo tick programado) para ver el diff.
                </div>
              ) : (
                <>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    Anterior: {activeDiff.previous_finished_at
                      ? new Date(activeDiff.previous_finished_at).toLocaleString()
                      : "—"}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <DiffKpi label="Nuevos" value={activeDiff.counts?.added ?? 0}
                            byKind={activeDiff.counts?.added_by_kind} accent="primary" icon={Plus} />
                    <DiffKpi label="Retirados" value={activeDiff.counts?.removed ?? 0}
                            byKind={activeDiff.counts?.removed_by_kind} accent="medium" icon={Minus} />
                    <DiffKpi label="Sin cambios" value={activeDiff.counts?.unchanged ?? 0} icon={CheckCircle2} />
                  </div>
                  {(activeDiff.added || []).length > 0 && (
                    <DiffList title="+ Añadidos en esta corrida" rows={activeDiff.added} tone="added" />
                  )}
                  {(activeDiff.removed || []).length > 0 && (
                    <DiffList title="− Ya no aparecen" rows={activeDiff.removed} tone="removed" />
                  )}
                </>
              )}
            </div>
          )}

          {/* Per-source status */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {["crtsh", "hackertarget", "shodan", "censys"].map(s => {
              const info = (activeJob.sources || {})[s] || {};
              return (
                <div key={s} className="border border-border px-3 py-2 text-xs" data-testid={`source-card-${s}`}>
                  <div className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">
                    {SOURCE_LABEL[s]}
                  </div>
                  {info.skipped ? (
                    <div className="text-sev-medium">Omitido ({info.reason || "sin key"})</div>
                  ) : info.ok ? (
                    <div className="text-sev-low">OK · {info.count ?? 0} resultados</div>
                  ) : info.error ? (
                    <div className="text-sev-critical truncate" title={info.error}>Error: {info.error}</div>
                  ) : (
                    <div className="text-muted-foreground">Sin ejecutar</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Counts */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <KpiCard label="Total activos" value={counts.total ?? 0} />
            <KpiCard label="Nuevos" value={counts.new ?? 0} accent="primary" />
            <KpiCard label="Subdominios" value={counts.subdomains ?? 0} />
            <KpiCard label="IPs públicas" value={counts.ips ?? 0} />
            <KpiCard label="Puertos únicos" value={counts.unique_ports ?? 0} />
          </div>

          {/* Filters + import */}
          {activeJob.status === "done" && (activeJob.results || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
              <div className="flex items-center gap-2 text-xs">
                <label className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">Tipo</label>
                <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)}
                        data-testid="filter-kind"
                        className="bg-background border border-border px-2 py-1 text-xs font-mono">
                  <option value="all">Todos</option>
                  <option value="domain">Dominios</option>
                  <option value="subdomain">Subdominios</option>
                  <option value="ip">IPs</option>
                </select>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <label className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">Fuente</label>
                <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
                        data-testid="filter-source"
                        className="bg-background border border-border px-2 py-1 text-xs font-mono">
                  <option value="all">Todas</option>
                  <option value="crtsh">crt.sh</option>
                  <option value="hackertarget">HackerTarget</option>
                  <option value="shodan">Shodan</option>
                  <option value="censys">Censys</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs font-mono cursor-pointer" data-testid="filter-new-only-wrap">
                <input type="checkbox" checked={filterNewOnly}
                       data-testid="filter-new-only"
                       onChange={(e) => setFilterNewOnly(e.target.checked)} />
                Solo nuevos
              </label>
              <div className="flex-1" />
              <button
                onClick={handleImport}
                disabled={selectedIds.size === 0 || importing}
                data-testid="import-selected-btn"
                className={cn(
                  "px-3 py-2 flex items-center gap-2 text-xs font-mono uppercase tracking-widest border transition-colors",
                  selectedIds.size === 0 || importing
                    ? "border-border text-muted-foreground cursor-not-allowed"
                    : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                )}>
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Import className="w-3 h-3" />}
                Importar seleccionados ({selectedIds.size})
              </button>
            </div>
          )}

          {/* Results table */}
          {activeJob.status === "done" && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono" data-testid="results-table">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground uppercase tracking-widest text-[10px]">
                    <th className="p-2 w-8">
                      <input type="checkbox"
                             checked={filteredResults.length > 0 && filteredResults.every(a => selectedIds.has(a.id))}
                             onChange={toggleAllVisible}
                             data-testid="select-all-visible" />
                    </th>
                    <th className="p-2">Tipo</th>
                    <th className="p-2">Activo</th>
                    <th className="p-2">Fuentes</th>
                    <th className="p-2">Estado</th>
                    <th className="p-2">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.length === 0 && (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-muted-foreground">
                        {filterNewOnly ? "No hay activos nuevos con los filtros actuales." : "Sin resultados."}
                      </td>
                    </tr>
                  )}
                  {filteredResults.map((a) => {
                    const Icon = KIND_ICON[a.kind] || Globe;
                    const disabled = a.imported;
                    return (
                      <tr key={a.id}
                          data-testid={`result-row-${a.id}`}
                          className={cn(
                            "border-b border-border/40 hover:bg-muted/20",
                            a.is_new && !a.imported && "bg-primary/5"
                          )}>
                        <td className="p-2">
                          <input type="checkbox"
                                 disabled={disabled}
                                 checked={selectedIds.has(a.id)}
                                 onChange={() => toggleId(a.id)}
                                 data-testid={`select-${a.id}`} />
                        </td>
                        <td className="p-2">
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                            <Icon className="w-3 h-3" /> {KIND_LABEL[a.kind]}
                          </span>
                        </td>
                        <td className="p-2 text-foreground">{a.value}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {(a.sources || []).map(s => (
                              <span key={s}
                                    className="px-1.5 py-0.5 border border-border text-[10px] uppercase tracking-widest bg-background">
                                {SOURCE_LABEL[s]}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-2">
                          {a.imported ? (
                            <span className="text-sev-low inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Importado
                            </span>
                          ) : a.is_new ? (
                            <span className="text-primary inline-flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> Nuevo
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Conocido</span>
                          )}
                        </td>
                        <td className="p-2 text-muted-foreground text-[10px]">
                          <AssetMetaSummary asset={a} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeJob.status === "error" && (
            <div className="p-4 border border-sev-critical/40 bg-sev-critical/5 text-sev-critical text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {activeJob.error || "El descubrimiento falló"}
            </div>
          )}
          {(activeJob.status === "queued" || activeJob.status === "running") && (
            <div className="p-4 border border-primary/40 bg-primary/5 text-primary text-sm flex items-center gap-2"
                 data-testid="job-running-banner">
              <Loader2 className="w-4 h-4 animate-spin" />
              Consultando fuentes en paralelo… esto suele tardar 20–60 segundos.
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    queued:  { text: "En cola",       cls: "text-muted-foreground" },
    running: { text: "Ejecutando",    cls: "text-primary" },
    done:    { text: "Completado",    cls: "text-sev-low" },
    error:   { text: "Error",         cls: "text-sev-critical" },
  };
  const s = map[status] || { text: status, cls: "text-muted-foreground" };
  return <span className={cn("font-mono", s.cls)}>{s.text}</span>;
}

function KpiCard({ label, value, accent }) {
  return (
    <div className="border border-border p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={cn("font-display text-3xl mt-1", accent === "primary" ? "text-primary" : "text-foreground")}>
        {value}
      </div>
    </div>
  );
}

function MiniKpi({ label, value, accent }) {
  return (
    <div className="text-center">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={cn("font-display text-lg leading-tight",
                         accent === "primary" ? "text-primary" : "text-foreground")}>
        {value}
      </div>
    </div>
  );
}

function DiffKpi({ label, value, byKind, accent, icon: Icon }) {
  const cls = accent === "primary" ? "text-primary"
            : accent === "medium"  ? "text-sev-medium"
            : "text-foreground";
  return (
    <div className="border border-border p-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={cn("font-display text-2xl mt-1", cls)}>{value}</div>
      {byKind && (
        <div className="text-[10px] font-mono text-muted-foreground mt-1">
          D:{byKind.domain} · S:{byKind.subdomain} · IP:{byKind.ip}
        </div>
      )}
    </div>
  );
}

function DiffList({ title, rows, tone }) {
  const toneCls = tone === "added" ? "text-primary" : "text-sev-medium";
  return (
    <div className="space-y-1">
      <div className={cn("text-[10px] font-mono uppercase tracking-widest", toneCls)}>{title}</div>
      <div className="max-h-40 overflow-y-auto text-xs font-mono space-y-0.5">
        {rows.slice(0, 25).map((r, i) => (
          <div key={`${r.kind}:${r.value}:${i}`} className="text-foreground">
            <span className="text-[10px] uppercase text-muted-foreground mr-2">{KIND_LABEL[r.kind] || r.kind}</span>
            {r.value}
          </div>
        ))}
        {rows.length > 25 && (
          <div className="text-[10px] text-muted-foreground">… y {rows.length - 25} más</div>
        )}
      </div>
    </div>
  );
}

function AssetMetaSummary({ asset }) {
  const parts = [];
  const ips = asset?.meta?.ips || [];
  if (ips.length) parts.push(`${ips.length} IP(s)`);
  const shodan = asset?.meta?.shodan;
  if (shodan?.ports?.length) parts.push(`Puertos: ${shodan.ports.slice(0, 5).join(", ")}${shodan.ports.length > 5 ? "…" : ""}`);
  if (shodan?.asn) parts.push(`ASN ${shodan.asn}`);
  if (shodan?.vulns?.length) parts.push(`${shodan.vulns.length} CVE(s)`);
  const censys = asset?.meta?.censys;
  if (censys?.asn && !shodan?.asn) parts.push(`ASN ${censys.asn}`);
  if (censys?.services?.length) {
    const ports = censys.services.map(s => s.port).filter(Boolean).slice(0, 5);
    if (ports.length) parts.push(`Servicios: ${ports.join(", ")}`);
  }
  return <span>{parts.join(" · ") || "—"}</span>;
}

// ---------------------------------------------------------------------------
// Inline job detail rendered inside the history modal (read-only view).
// ---------------------------------------------------------------------------
function HistoryJobDetail({ job, clientName }) {
  const counts = job.counts || {};
  const results = job.results || [];
  const newAssets = results.filter(a => a.is_new);
  const preview = newAssets.length > 0 ? newAssets : results;
  return (
    <div className="space-y-4" data-testid="history-detail">
      <div className="text-xs font-mono text-muted-foreground">
        <span className="text-foreground">{clientName || "—"}</span>{" · "}
        Job {job.id.slice(0, 8)} · <StatusBadge status={job.status} />
        {job.finished_at && (
          <> · Finalizado {new Date(job.finished_at).toLocaleString()}</>
        )}
        {job.schedule_id && <> · Ejecución programada</>}
      </div>

      {/* Per-source */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {["crtsh", "hackertarget", "shodan", "censys"].map(s => {
          const info = (job.sources || {})[s] || {};
          return (
            <div key={s} className="border border-border px-3 py-2 text-xs">
              <div className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">
                {SOURCE_LABEL[s]}
              </div>
              {info.skipped ? (
                <div className="text-sev-medium">Omitido ({info.reason || "sin key"})</div>
              ) : info.ok ? (
                <div className="text-sev-low">OK · {info.count ?? 0} resultados</div>
              ) : info.error ? (
                <div className="text-sev-critical truncate" title={info.error}>Error: {info.error}</div>
              ) : (
                <div className="text-muted-foreground">Sin ejecutar</div>
              )}
            </div>
          );
        })}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard label="Total activos" value={counts.total ?? 0} />
        <KpiCard label="Nuevos" value={counts.new ?? 0} accent="primary" />
        <KpiCard label="Subdominios" value={counts.subdomains ?? 0} />
        <KpiCard label="IPs públicas" value={counts.ips ?? 0} />
        <KpiCard label="Puertos únicos" value={counts.unique_ports ?? 0} />
      </div>

      {/* Results (read-only) */}
      {job.status === "done" && preview.length > 0 && (
        <div className="overflow-x-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
            {newAssets.length > 0
              ? `Activos nuevos (${newAssets.length})`
              : `Todos los activos (${results.length})`}
          </div>
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground uppercase tracking-widest text-[10px]">
                <th className="p-2">Tipo</th>
                <th className="p-2">Activo</th>
                <th className="p-2">Fuentes</th>
                <th className="p-2">Estado</th>
                <th className="p-2">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {preview.slice(0, 100).map((a) => {
                const Icon = KIND_ICON[a.kind] || Globe;
                return (
                  <tr key={a.id} className={cn(
                    "border-b border-border/40",
                    a.is_new && !a.imported && "bg-primary/5"
                  )}>
                    <td className="p-2">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                        <Icon className="w-3 h-3" /> {KIND_LABEL[a.kind]}
                      </span>
                    </td>
                    <td className="p-2 text-foreground">{a.value}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {(a.sources || []).map(s => (
                          <span key={s} className="px-1.5 py-0.5 border border-border text-[10px] uppercase tracking-widest bg-background">
                            {SOURCE_LABEL[s]}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-2">
                      {a.imported ? (
                        <span className="text-sev-low inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Importado
                        </span>
                      ) : a.is_new ? (
                        <span className="text-primary inline-flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Nuevo
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Conocido</span>
                      )}
                    </td>
                    <td className="p-2 text-muted-foreground text-[10px]">
                      <AssetMetaSummary asset={a} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {preview.length > 100 && (
            <div className="mt-2 text-[10px] text-muted-foreground">
              Mostrando los primeros 100 activos de {preview.length}. Cierra el modal
              y usa "Ejecutar ahora" del cliente para ver el detalle completo con filtros e importación.
            </div>
          )}
        </div>
      )}

      {job.status === "error" && (
        <div className="p-4 border border-sev-critical/40 bg-sev-critical/5 text-sev-critical text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {job.error || "El descubrimiento falló"}
        </div>
      )}
      {(job.status === "queued" || job.status === "running") && (
        <div className="p-4 border border-primary/40 bg-primary/5 text-primary text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Este job aún está en ejecución.
        </div>
      )}
    </div>
  );
}
